import { useEffect, useRef, useState, type ReactNode } from "react";
import { CatalogList, CatalogPicker } from "@core/components/CatalogPicker";
import { ErrorNote } from "@core/components/ErrorNote";
import { BudgetBar, List, ListRow } from "@core/components/List";
import { LongText } from "@core/components/LongText";
import { Segments } from "@core/components/Segments";
import { Sheet } from "@core/components/Sheet";
import { Stepper } from "@core/components/Stepper";
import { Value, ValueRow } from "@core/components/Value";
import { placementTargets, previewOperation } from "@core/api/queries";
import type { PlacementTargetView } from "@core/api/types";
import { useDraftValue } from "@core/character";
import { number } from "@core/text";
import { CustomValuesForm } from "./customEntry";
import { CrafterFields } from "./crafting";
import { ParamField, RelicPowers, type PowerChoice } from "./relicPowers";
import { kindLabels, page, resourceLabel, useSm, type ArtifactState, type Crafter, type CustomKind, type InventoryView, type ItemChoiceRow, type ItemKind, type ItemOrigin, type ProjectileView, type RelicSplit, type WeaponView } from "./view";

/** A choice as the operation stores it: entry with what is chosen for it. */
const choicePayload = (rows: ItemChoiceRow[]) => rows.map((r) => ({ id: r.id, param: r.param, detail: r.detail }));

const newRow = (id: string, name: string, qualityCost?: number): ItemChoiceRow => ({ index: -1, id, name, qualityCost, param: null, paramName: null, detail: null, detailName: null });

const catalogKinds: { kind: Exclude<ItemKind, "custom" | "ammo">; catalog: string; label: string }[] = [
  { kind: "item", catalog: "items", label: "Gegenstände" },
  { kind: "weapon", catalog: "weapons", label: "Waffen" },
  { kind: "armor", catalog: "armor", label: "Rüstungen" },
  { kind: "shield", catalog: "shields", label: "Schilde" },
];

/** What an item counts as for the appliesTo of improvements (GRW p. 142); a weapon may also be carried as parry weapon. */
const improvementKinds: Record<ItemKind, string[]> = { weapon: ["waffe", "parierwaffe"], armor: ["ruestung"], shield: ["schild"], item: ["gegenstand"], custom: [], ammo: [] };

/** Rows of items; tapping a row opens the editor. */
export function InventoryRows(props: { items: InventoryView[]; showOrigin?: boolean; empty?: ReactNode; onEdit: (item: InventoryView) => void }) {
  return (
    <List aria-label="Gegenstände">
      {props.items.length === 0 && <li className="list-empty">{props.empty ?? "Noch nichts."}</li>}
      {props.items.map((item) => (
        <InventoryRow key={item.instance} item={item} showOrigin={props.showOrigin} onEdit={props.onEdit} />
      ))}
    </List>
  );
}

/** One item: name, marks, kind and load, the value of the line; tapping it opens the editor, the actions sit beside it. */
export function InventoryRow(props: { item: InventoryView; showOrigin?: boolean; container?: string | null; onEdit: (item: InventoryView) => void; actions?: ReactNode; as?: "li" | "div"; selected?: boolean }) {
  const { value, display, language, view } = useSm();
  const { item } = props;
  const usedBy = view.inventory.filter((w) => w.ammo === item.instance).map((w) => w.customName ?? w.name);
  return (
    <ListRow
      as={props.as}
      selected={props.selected}
      title={
        <>
          {item.quantity > 1 && <span className="muted">{item.quantity}× </span>}
          {item.customName ?? item.name}
          {item.customName && item.customName !== item.name && <span className="muted"> ({item.name})</span>}
        </>
      }
      badges={
        <>
          {props.container && <span className="badge path-chip">im {props.container}</span>}
          {item.custom && <span className="badge">Eigenbau</span>}
          {item.quality !== 0 && <span className="badge">Q {item.quality}</span>}
          {item.relic && <span className="badge">{item.relicLabel ?? "Relikt"}</span>}
          {item.creatureLabel && <span className="badge">{item.creatureLabel}</span>}
          {item.artifact && <span className="badge">{item.artifact.kind === "schriftrolle" ? "Schriftrolle" : "Artefakt"}</span>}
          {item.personalizations.length > 0 && <span className="badge">personalisiert</span>}
          {item.blessed && <span className="badge">gesegnet</span>}
          {item.equipped && item.wearable && <span className="badge">angelegt</span>}
          {item.parryWeapon && <span className="badge">Parierwaffe</span>}
          {item.baseEquipment && (
            <span className="badge" title="Grundausstattung">
              GA
            </span>
          )}
        </>
      }
      subtitle={[
        kindLabels[item.kind],
        props.showOrigin ? item.originLabel : null,
        item.material?.name,
        item.kind === "ammo" && usedBy.length > 0 ? `für ${usedBy.join(", ")}` : null,
        item.load != null ? `Last ${number(item.load)}` : null,
        item.note,
      ]
        .filter(Boolean)
        .join(" · ")}
      value={<span data-value={item.totalKey}>{display(value(item.totalKey))}</span>}
      onClick={() => props.onEdit(item)}
      ariaLabel={`${item.customName ?? item.name} bearbeiten`}
      actions={props.actions}
      below={
        item.description || item.features.some((f) => f.description) || item.artifactProperties.some((a) => a.description) ? (
          <>
            <LongText value={item.description} language={language} />
            {item.features.map((f) => (
              <LongText key={f.id} value={f.description} language={language} label={f.name} />
            ))}
            {item.artifactProperties.map((a, i) => (
              <LongText key={`${a.id}-${i}`} value={a.description} language={language} label={a.name} />
            ))}
          </>
        ) : undefined
      }
    />
  );
}

/**
 * Adds an item from the catalogs, a custom one or a stock of ammunition. During creation the origin picks the budget (GRW p. 26);
 * in career mode it decides what is booked: bought, crafted from material (p. 143) or received.
 */
export function AddItemSheet(props: {
  title: string;
  /** Fixed origin, or a choice when omitted. */
  origin?: ItemOrigin;
  /** Adds the item as relic of this instance of the resource Relikt (GRW p. 79). */
  relic?: string;
  /** Only these kinds, e.g. weapons for the start weapon. */
  kinds?: ItemKind[];
  /** Ammunition added from a weapon is assigned to it right away. */
  forWeapon?: InventoryView;
  onClose: () => void;
}) {
  const { view, apply, confirm, character, error, clearError, value } = useSm();
  const creation = character.mode === "creation";
  const purchaseChange = value(view.startKit.purchaseChangeKey)?.number ?? 0;
  // „Alle“ joins the catalogs of every kind; with a restricted choice, e.g. only weapons for the start weapon, there is none.
  const kinds: (ItemKind | "all" | "kit")[] = props.kinds ?? ["all", "item", "weapon", "armor", "shield", "custom", "ammo", "kit"];
  const [kind, setKind] = useState<ItemKind | "all" | "kit">(kinds[0] ?? "item");
  // A kit with choices waits for them before it is added (MSK p. 80; SM-G-173).
  const [kit, setKit] = useState<{ id: string; label: string; choices: KitChoiceView[] } | null>(null);
  const [kitChoices, setKitChoices] = useState<Record<string, number>>({});
  // The search has the focus when the sheet opens; after a change of tab the focus stays on the tab.
  const [tabChanged, setTabChanged] = useState(false);
  const [origin, setOrigin] = useState<ItemOrigin>(props.origin ?? "purchased");
  const [name, setName] = useState("");
  const [ammoFor, setAmmoFor] = useState(view.ammunition.find((a) => a.ref === props.forWeapon?.ref)?.ref ?? "");
  const [projectile, setProjectile] = useState("");
  const [price, setPrice] = useState("");
  const [load, setLoad] = useState("");
  const [note, setNote] = useState("");
  const [quantity, setQuantity] = useState(kind === "ammo" ? 20 : 1);
  const [adjust, setAdjust] = useState("");
  const [paid, setPaid] = useState("");
  const [trade, setTrade] = useState<TradeState>(emptyTrade);
  // A piece made as artifact books its material with the artifact (GRW p. 255; SM-F-089, RA-ZAUBB-3).
  const [artifact, setArtifact] = useState<ArtifactState | null>(null);
  const [busy, setBusy] = useState(false);
  // A custom item without values, or a weapon, armour, shield or item with own values (concept of items, stage 4).
  const [withValues, setWithValues] = useState<CustomKind | null>(null);
  useClearErrorOnClose(clearError);
  const pays = !creation && (origin === "purchased" || origin === "crafted");
  const madeAsArtifact = pays && origin === "crafted" && kind !== "ammo" && kind !== "kit" && artifact !== null;
  const add = async (payload: Record<string, unknown>, type = "sm.addItem") => {
    if (busy) return;
    setBusy(true);
    try {
      const traded = pays && origin === "purchased" ? tradePayload(trade, view.trading !== null) : null;
      const procurement = pays ? { adjust: adjust.trim() || null, paid: paid.trim() || null, ...(traded ? { trade: traded } : {}) } : {};
      const target = props.forWeapon && payload.kind === "ammo" ? { forWeapon: props.forWeapon.instance } : {};
      const made = madeAsArtifact && type === "sm.addItem" ? { artifact } : {};
      const operation = { type, payload: { ...payload, origin, relic: props.relic ?? null, ...procurement, ...target, ...made } };
      // What a purchase books, and whether the money suffices, shows the preview before anything is booked.
      if (pays) {
        confirm(operation, origin === "crafted" ? "Herstellen" : "Kaufen");
        props.onClose();
      } else if (await apply(operation)) props.onClose();
    } finally {
      setBusy(false);
    }
  };
  const current = catalogKinds.find((k) => k.kind === kind);
  const originChoice =
    props.origin === undefined &&
    (creation ? (
      <label className="field">
        <span>Herkunft</span>
        <select value={origin} onChange={(e) => setOrigin(e.target.value as ItemOrigin)}>
          {view.startKit.origins.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    ) : (
      <div className="stack small-gap">
        <Segments ariaLabel="Herkunft" options={view.startKit.careerOrigins.map((o) => ({ value: o.id, label: o.label }))} value={origin} onChange={setOrigin} />
        {pays && origin === "purchased" && <TradeFields buying value={trade} onChange={setTrade} />}
        {pays ? (
          <div className="inline-form two">
            <label className="field">
              <span>Preisänderung in %</span>
              <input
                inputMode="decimal"
                value={adjust}
                placeholder={origin === "purchased" && purchaseChange !== 0 ? `Vorgabe ${purchaseChange}` : "z. B. −10"}
                onChange={(e) => setAdjust(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Bezahlt</span>
              <input value={paid} placeholder={origin === "crafted" ? `${view.startKit.materialShare ?? ""} des Preises` : "Preis mit Qualität"} onChange={(e) => setPaid(e.target.value)} />
            </label>
            <span className="muted small">
              {origin === "crafted"
                ? `Gebucht werden die Materialkosten (${page(view.startKit.craftingSource)}), geändert um die Prozentangabe; ein Betrag unter „Bezahlt“ ersetzt beides.`
                : "Gebucht wird der Preis mit Qualität, geändert um die Prozentangabe, etwa nach dem Handeln (GRW S. 110); ein Betrag unter „Bezahlt“ ersetzt beides."}
            </span>
            {origin === "crafted" && kind !== "ammo" && kind !== "kit" && (
              <div className="stack small-gap">
                <label className="option">
                  <input type="checkbox" checked={artifact !== null} onChange={(e) => setArtifact(e.target.checked ? { quality: 1, properties: [] } : null)} />
                  <span>als Artefakt herstellen (GRW S. 254)</span>
                </label>
                {artifact && (
                  <>
                    <ArtifactFields value={artifact} onChange={setArtifact} />
                    <span className="muted small">
                      Das Material kostet dann {view.startKit.materialShare ?? "einen Anteil"} des Preises samt Aufschlag der Artefaktqualität, bei einer Schriftrolle des
                      Preises je Zaubergrad (GRW S. 255). Wird ein Stück erst später im Editor zum Artefakt, bucht „Bezahlen“ den ganzen Aufpreis.
                    </span>
                  </>
                )}
              </div>
            )}
            {origin === "crafted" && view.startKit.craftingHints.length > 0 && (
              <ul className="muted small" aria-label="Proben beim Herstellen">
                {view.startKit.craftingHints.map((h) => (
                  <li key={h.label}>
                    <strong>{h.label}:</strong> {h.text} ({page(h.source)})
                  </li>
                ))}
              </ul>
            )}
            {origin === "purchased" && purchaseChange !== 0 && (
              <ValueRow valueKey={view.startKit.purchaseChangeKey} label="Ohne Angabe gilt die Preisänderung aus Meisterschaften in %" />
            )}
          </div>
        ) : (
          <span className="muted small">Gefunden, geschenkt oder als Beute: Es wird kein Geld gebucht.</span>
        )}
      </div>
    ));
  const ammoType = kind === "ammo" ? view.ammunition.find((a) => a.ref === ammoFor) : undefined;
  const projectiles = (view.projectiles ?? []).filter((p) => ammoType?.kind && p.ammunition.includes(ammoType.kind));
  const ammoHint =
    kind === "ammo" &&
    (ammoType
      ? `Je Stück ${ammoType.priceText ?? "ohne Preis"} (${page(ammoType.source)}); ein eigener Preis ersetzt ihn.`
      : `Ohne Waffenart legt die Spielleitung den Preis fest.${props.forWeapon ? "" : " Den Vorrat ordnest du danach im Editor einer Schusswaffe zu."}`);

  return (
    <Sheet title={props.title} onClose={props.onClose} wide>
      <div className="stack small-gap">
        <ErrorNote error={error} />
        {kinds.length > 1 && (
          <Segments
            role="tablist"
            ariaLabel="Art"
            options={kinds.map((k) => ({
              value: k,
              label: k === "all" ? "Alle" : k === "custom" ? "Eigener" : k === "ammo" ? "Munition" : k === "kit" ? "Zusammenstellung" : (catalogKinds.find((c) => c.kind === k)?.label ?? k),
            }))}
            value={kind}
            onChange={(k) => {
              setKind(k);
              setTabChanged(true);
            }}
          />
        )}
        {originChoice}
        {kind === "custom" && (
          <Segments
            ariaLabel="Werte"
            options={[{ value: null, label: "Ohne Werte" }, ...view.customForm.kinds.map((k) => ({ value: k.id, label: `${k.name} mit Werten` }))]}
            value={withValues}
            onChange={setWithValues}
          />
        )}
        {kind === "custom" && withValues ? (
          <CustomValuesForm key={withValues} kind={withValues} submitLabel="Hinzufügen" busy={busy} onSubmit={(entry) => void add({ kind: withValues, entry })} />
        ) : kind === "custom" || kind === "ammo" ? (
          <div className="stack small-gap">
            {kind === "ammo" && (
              <label className="field">
                <span>Munition für</span>
                <select value={ammoFor} onChange={(e) => setAmmoFor(e.target.value)}>
                  <option value="">ohne Waffenart</option>
                  {view.ammunition.map((a) => (
                    <option key={a.ref} value={a.ref}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {ammoHint && <p className="muted small">{ammoHint}</p>}
            {kind === "ammo" && projectiles.length > 0 && (
              <ProjectileField projectiles={projectiles} value={projectile} onChange={setProjectile} />
            )}
            <label className="field">
              <span>Name</span>
              <input
                autoFocus={!tabChanged}
                value={name}
                placeholder={kind === "ammo" ? (ammoType ? `Munition (${ammoType.name})` : "z. B. Pfeile") : undefined}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <div className="inline-form two">
              <label className="field">
                <span>Preis je Stück</span>
                <input value={price} placeholder={ammoType?.priceText ?? "z. B. 3 L 50 T"} onChange={(e) => setPrice(e.target.value)} />
              </label>
              <div className="field">
                <span>Anzahl</span>
                <Stepper label="Anzahl" value={quantity} min={1} onChange={setQuantity} />
              </div>
            </div>
            <label className="field">
              <span>Last</span>
              <input inputMode="decimal" value={load} placeholder="z. B. 1 oder 0,5" onChange={(e) => setLoad(e.target.value)} />
            </label>
            <label className="field">
              <span>Notiz</span>
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            <div className="actions">
              <button
                type="button"
                className="button primary"
                disabled={(!name.trim() && !ammoType) || busy}
                onClick={() =>
                  void add({
                    kind,
                    ref: ammoType?.ref ?? null,
                    name: name.trim() || null,
                    quantity,
                    price: price.trim() || null,
                    load: load.trim() || null,
                    note: note.trim() || null,
                    ...(kind === "ammo" && projectiles.some((p) => p.id === projectile) ? { projectile } : {}),
                  })
                }
              >
                Hinzufügen
              </button>
            </div>
          </div>
        ) : kind === "kit" ? (
          kit ? (
            <div className="stack small-gap">
              <strong>{kit.label}</strong>
              {kit.choices.map((choice) => (
                <label key={choice.id} className="field">
                  <span>{choice.label}</span>
                  <select value={kitChoices[choice.id] ?? 0} onChange={(e) => setKitChoices({ ...kitChoices, [choice.id]: Number(e.target.value) })}>
                    {choice.options.map((option, index) => (
                      <option key={index} value={index}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
              <div className="actions">
                <button type="button" className="button secondary" onClick={() => setKit(null)}>
                  Zurück
                </button>
                <button
                  type="button"
                  className="button primary"
                  disabled={busy}
                  onClick={() => void add({ kit: kit.id, choices: Object.fromEntries(kit.choices.map((c) => [c.id, kitChoices[c.id] ?? 0])) }, "sm.addKit")}
                >
                  Hinzufügen
                </button>
              </div>
            </div>
          ) : (
            <CatalogList
              key="kits"
              type="kits"
              autoFocus={!tabChanged}
              onPick={(item) => {
                const choices = ((item.details as { choices?: KitChoiceView[] } | undefined)?.choices ?? []) as KitChoiceView[];
                if (choices.length > 0) setKit({ id: item.id, label: item.label.de, choices });
                else void add({ kit: item.id }, "sm.addKit");
              }}
            />
          )
        ) : kind === "all" ? (
          <CatalogList key="equipment" type="equipment" autoFocus={!tabChanged} onPick={(item) => void add({ kind: item.details?.kind as ItemKind, ref: item.id })} />
        ) : (
          current && (
            <CatalogList
              key={current.catalog}
              type={current.catalog}
              autoFocus={!tabChanged}
              facets={current.kind === "item" ? [{ key: "category", label: "Kategorie", options: view.itemCategories.map((c) => ({ value: c.id, label: c.name.replace(/^Ausrüstung für /, "") })) }] : undefined}
              onPick={(item) => void add({ kind: current.kind, ref: item.id })}
            />
          )
        )}
      </div>
    </Sheet>
  );
}

/**
 * The instance of Kreatur an animal comes from instead of being bought (GRW p. 78, 191; SM-G-192): one instance is one creature, so an
 * instance another item comes from is offered but not choosable. Shown only when the character has such an instance or the item names one.
 */
export function CreatureField(props: { item: InventoryView | null; value: string; onChange: (value: string) => void }) {
  const { view } = useSm();
  const creatures = view.resources.filter((r) => r.creature);
  const current = props.item?.creature;
  if (creatures.length === 0 && !current) return null;
  return (
    <>
      <label className="field">
        <span>Aus einer Ressource</span>
        <select value={props.value} onChange={(e) => props.onChange(e.target.value)}>
          <option value="">keine</option>
          {creatures.map((r) => {
            const other = r.animal && r.animal !== props.item?.instance ? view.inventory.find((i) => i.instance === r.animal) : undefined;
            return (
              <option key={r.key} value={r.key} disabled={other !== undefined}>
                {resourceLabel(view, r.key)}
                {other ? ` – schon ${other.customName ?? other.name}` : ""}
              </option>
            );
          })}
          {current && !creatures.some((r) => r.key === current) && <option value={current}>{props.item?.creatureLabel ?? current} (nicht vorhanden)</option>}
        </select>
      </label>
      <p className="muted small">Ein gekauftes Tier braucht keine Ressource. Aus einer Instanz der Ressource Kreatur stammt genau ein Tier; es kostet dann nichts weiter.</p>
    </>
  );
}

/**
 * Kind, artifact quality and properties of a simple artifact, with the pickers for properties and anchored spells (GRW p. 254–255);
 * used by the editor and, for a piece made as artifact, when adding it.
 */
function ArtifactFields(props: { value: ArtifactState; onChange: (artifact: ArtifactState) => void; known?: InventoryView["artifactProperties"] }) {
  const { view } = useSm();
  const artifact = props.value;
  const setArtifact = props.onChange;
  // Names, levels and anchored spells of the properties as shown; the stored artifact keeps only id, level and spell.
  const [propertyInfo, setPropertyInfo] = useState<Record<string, { name: string; maxLevel?: number | null; spellGrade?: number | null }>>(() =>
    Object.fromEntries((props.known ?? []).map((a) => [a.id, { name: a.name, maxLevel: a.maxLevel, spellGrade: a.spellGrade }])),
  );
  const [spellNames, setSpellNames] = useState<Record<string, string>>(() =>
    Object.fromEntries((props.known ?? []).filter((a) => a.param && a.paramName).map((a) => [a.param!, a.paramName!])),
  );
  const [anchoring, setAnchoring] = useState<number | null>(null);
  const [pickingProperty, setPickingProperty] = useState(false);
  const artifactKind = artifact.kind ?? "strukturgeber";
  const scroll = artifactKind === "schriftrolle";
  return (
    <div className="stack small-gap">
      <Segments
        ariaLabel="Artefaktart"
        options={view.artifactKinds.map((k) => ({ value: k.id, label: k.name }))}
        value={artifactKind}
        onChange={(id) => setArtifact({ ...artifact, kind: id === "strukturgeber" ? null : id })}
      />
      <div className="row spread">
        <span>Artefaktqualität</span>
        <Stepper label="Artefaktqualität" value={artifact.quality} min={1} onChange={(q) => setArtifact({ ...artifact, quality: q })} />
      </div>
      <List aria-label="Artefakteigenschaften">
        {artifact.properties.length === 0 && <li className="list-empty">Noch keine Eigenschaft gewählt.</li>}
        {artifact.properties.map((property, index) => {
          const info = propertyInfo[property.id];
          return (
            <ListRow
              key={`${property.id}-${index}`}
              title={info?.name ?? property.id}
              subtitle={
                info?.spellGrade != null ? (
                  <button type="button" className="link" onClick={() => setAnchoring(index)}>
                    {property.param ? `Zauber: ${spellNames[property.param] ?? property.param}` : `Zauber des Grades ${info.spellGrade} wählen`}
                  </button>
                ) : info?.maxLevel != null ? (
                  `höchstens Stufe ${info.maxLevel}`
                ) : undefined
              }
              actions={
                <>
                  {info?.maxLevel != null && info.maxLevel > 1 && (
                    <Stepper
                      label={`Stufe ${info.name}`}
                      value={property.level}
                      min={1}
                      onChange={(level) => setArtifact({ ...artifact, properties: artifact.properties.map((p, i) => (i === index ? { ...p, level } : p)) })}
                    />
                  )}
                  <button type="button" className="link danger" onClick={() => setArtifact({ ...artifact, properties: artifact.properties.filter((_, i) => i !== index) })}>
                    Entfernen
                  </button>
                </>
              }
            />
          );
        })}
      </List>
      <button type="button" className="button secondary" onClick={() => setPickingProperty(true)}>
        Eigenschaft hinzufügen
      </button>
      <span className="muted small">
        {scroll
          ? `Eine Schriftrolle trägt ${view.scrolls?.maxEffects === 1 ? "eine Wirkung" : `bis zu ${view.scrolls?.maxEffects ?? "?"} Wirkungen`} und ist nach einmaligem Auslösen verbraucht; sie kostet je Grad des verankerten Zaubers (${page(view.scrolls?.source)}).`
          : "Ein Strukturgeber trägt mehrere Wirkungen; zusammen kosten sie höchstens so viele Artefaktpunkte, wie die Artefaktqualität beträgt (GRW S. 254)."}
      </span>
      {pickingProperty && (
        <CatalogPicker
          type="artifact-properties"
          title="Artefakteigenschaft"
          onClose={() => setPickingProperty(false)}
          unavailable={(p) => {
            const kinds = (p.details as { artifactKinds?: string[] } | undefined)?.artifactKinds ?? [];
            return kinds.length > 0 && !kinds.includes(artifactKind) ? `nicht bei ${view.artifactKinds.find((k) => k.id === artifactKind)?.name ?? artifactKind}` : null;
          }}
          onPick={(p) => {
            setPickingProperty(false);
            const details = p.details as { maxLevel?: number | null; spellGrade?: number | null } | undefined;
            setPropertyInfo({ ...propertyInfo, [p.id]: { name: p.label.de, maxLevel: details?.maxLevel, spellGrade: details?.spellGrade } });
            setArtifact({ ...artifact, properties: [...artifact.properties, { id: p.id, level: 1 }] });
          }}
        />
      )}
      {anchoring !== null && (
        <CatalogPicker
          type="spells"
          title="Verankerter Zauber"
          unavailable={(spell) => {
            const grade = propertyInfo[artifact.properties[anchoring]?.id ?? ""]?.spellGrade;
            const schools = (spell.details as { schools?: { grade: number }[] } | undefined)?.schools ?? [];
            return grade != null && !schools.some((school) => school.grade === grade) ? `nicht Grad ${grade}` : null;
          }}
          onClose={() => setAnchoring(null)}
          onPick={(spell) => {
            setSpellNames({ ...spellNames, [spell.id]: spell.label.de });
            setArtifact({ ...artifact, properties: artifact.properties.map((p, i) => (i === anchoring ? { ...p, param: spell.id } : p)) });
            setAnchoring(null);
          }}
        />
      )}
    </div>
  );
}

/** Edits one item: name, quantity, origin and relic, quality with improvements, material and personalization, artifact, ammunition, price and note. */
export function ItemEditor({ item, onClose }: { item: InventoryView; onClose: () => void }) {
  const { apply, choose, confirm, view, character, error, clearError, value, display, budget, money } = useSm();
  const live = view.inventory.find((i) => i.instance === item.instance) ?? item;
  const creation = character.mode === "creation";
  useClearErrorOnClose(clearError);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState(item.customName ?? "");
  const [quantity, setQuantity] = useState(item.quantity);
  const [quality, setQuality] = useState(item.quality);
  const [equipped, setEquipped] = useState(item.equipped);
  const [origin, setOrigin] = useState<ItemOrigin>(item.origin);
  const [relic, setRelic] = useState(item.relic);
  const [relicSplit, setRelicSplit] = useState<RelicSplit | null>(item.relicSplit);
  const relics = view.resources.filter((r) => r.relicPoints);
  const [creature, setCreature] = useState(item.creature ?? "");
  const [ammo, setAmmo] = useState<string | null | undefined>(undefined);
  const [projectile, setProjectile] = useState(item.projectile ?? "");
  const stockKind = item.kind === "ammo" ? view.ammunition.find((a) => a.ref === item.ref)?.kind : undefined;
  const projectileOptions = (view.projectiles ?? []).filter((p) => stockKind && p.ammunition.includes(stockKind));
  const [addingAmmo, setAddingAmmo] = useState(false);
  const stocks = view.inventory.filter((i) => i.kind === "ammo");
  const usedBy = view.inventory.filter((w) => w.ammo === item.instance);
  const [parry, setParry] = useState(item.parryWeapon);
  const [note, setNote] = useState(item.note ?? "");
  const [price, setPrice] = useState(item.customPrice ?? "");
  const [load, setLoad] = useState(item.customLoad == null ? "" : number(item.customLoad));
  const [hardness, setHardness] = useState(item.customHardness == null ? "" : String(item.customHardness));
  const [baseEquipment, setBaseEquipment] = useState(item.baseEquipment);
  const [parent, setParent] = useState(item.parent);
  const [targets, setTargets] = useState<PlacementTargetView[] | null>(null);
  useEffect(() => {
    if (item.parent === null) return;
    let cancelled = false;
    placementTargets(character.id, [item.instance]).then(
      (list) => !cancelled && setTargets(list),
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [character.id, item.instance, item.parent]);
  const [focusSkill, setFocusSkill] = useState(item.focus?.skill ?? "");
  const [focusName, setFocusName] = useState(item.focus?.name ?? "");
  const [selling, setSelling] = useState(false);
  const [editingValues, setEditingValues] = useState(false);
  const [improvements, setImprovements] = useState<ItemChoiceRow[] | null>(null);
  const currentImprovements = improvements ?? item.improvements;
  const [powers, setPowers] = useState<PowerChoice[] | null>(null);
  const currentPowers = powers ?? item.legendaryPowers.map((p) => ({ id: p.id, param: p.param, detail: p.detail }));
  const [materialFromRelic, setMaterialFromRelic] = useState(item.materialFromRelic);
  const [picking, setPicking] = useState(false);
  const [artifact, setArtifact] = useState(item.artifact);
  const [material, setMaterial] = useState(item.material);
  const [pickingMaterial, setPickingMaterial] = useState(false);
  const [materialPrice, setMaterialPrice] = useState(item.materialPrice ?? "");
  const materialChanged = (material?.id ?? null) !== (item.material?.id ?? null);
  const materialSuggestion = useDraftValue(materialChanged && material ? { type: "sm.updateItem", payload: { instance: item.instance, material: material.id } } : null, live.materialSurchargeKey);
  const materialPaid = !creation && material !== null && (materialChanged || materialPrice.trim() !== (item.materialPrice ?? ""));
  const [materialParam, setMaterialParam] = useState(item.materialParam);
  const materialChoice = view.materialChoices.find((m) => m.id === material?.id)?.parameter ?? null;
  const [personalizations, setPersonalizations] = useState<ItemChoiceRow[]>(item.personalizations);
  const [personalizedBySelf, setPersonalizedBySelf] = useState(item.personalizedBySelf);
  const [blessed, setBlessed] = useState(item.blessed);
  const [fullSet, setFullSet] = useState(item.fullSet);
  const [variants, setVariants] = useState<string[]>(item.variants);
  const [crafter, setCrafter] = useState<Crafter | null>(item.crafter);
  const kinds = item.kinds.length > 0 ? item.kinds : improvementKinds[item.kind];
  const needsFocus = item.kind !== "weapon" && currentImprovements.some((i) => view.focusImprovements.includes(i.id));
  const fits = (appliesTo: string[]) => appliesTo.length === 0 || appliesTo.some((a) => kinds.includes(a));
  const personalizationDefs = new Map(view.personalizations.map((p) => [p.id, p]));
  const offeredPersonalizations = view.personalizations.filter((p) => fits(p.appliesTo));
  // A new personalization is booked in career mode, so it goes through the preview like the material (SM-G-113).
  const personalizationAdded = personalizations.some((p) => !item.personalizations.some((b) => b.id === p.id && b.param === p.param && b.detail === p.detail));
  const ownWorkPossible = personalizations.some((p) => personalizationDefs.get(p.id)?.ownWork);
  const improvementChoices = new Map(view.improvementChoices.map((c) => [c.id, c]));
  const chosenRelic = relics.find((r) => r.key === relic);
  // A material point is not possible when the material quality leaves no relic point for it, e.g. Mondstahl (MSK p. 100; SM-G-103).
  const materialPointBlocked = material !== null && (material.quality ?? 0) > (view.relicMaterialMaxQuality ?? Number.POSITIVE_INFINITY);
  const fromRelic = relic !== null && material !== null && (material.quality ?? 0) > 0 && !materialPointBlocked && materialFromRelic;
  const relicDraft =
    relic !== item.relic ||
    quality !== item.quality ||
    artifact?.quality !== item.artifact?.quality ||
    relicSplit?.quality !== item.relicSplit?.quality ||
    relicSplit?.artifactQuality !== item.relicSplit?.artifactQuality ||
    materialChanged ||
    fromRelic !== item.materialFromRelic;
  const relicDraftOperation = relicDraft && chosenRelic
    ? { type: "sm.updateItem", payload: { instance: item.instance, relic, quality, artifact, relicSplit, material: material?.id ?? null, materialFromRelic: fromRelic } }
    : null;
  // Improvements and powers share the quality; the draft carries what decides their cost and places (SM-G-098, SM-G-103).
  const qualityDraft = improvements || powers || relicDraft || blessed !== item.blessed
    ? {
        type: "sm.updateItem",
        payload: { instance: item.instance, relic, quality, blessed, material: material?.id ?? null, materialFromRelic: fromRelic, improvements: choicePayload(currentImprovements), legendaryPowers: currentPowers },
      }
    : null;
  const qualitySpent = useDraftValue(qualityDraft, live.qualityKey);
  // The levels improvements may spend come from the engine, with material and blessing (RA-AUSR-2, RA-AUSG-11).
  const qualityBudget = useDraftValue(qualityDraft, live.qualityBudgetKey);
  // Whether the improvements act reversed is the engine's to say: a material of positive quality lifts a negative chosen one (MSK p. 94).
  const reversed = (useDraftValue(qualityDraft, live.negativeQualityKey) ?? 0) > 0;
  const withImprovements = (qualityBudget ?? 0) !== 0 || currentImprovements.length > 0;
  const powerCost = useDraftValue(qualityDraft, live.powerCostKey);
  const relicSpent = useDraftValue(relicDraftOperation, chosenRelic?.relicPoints?.spent ?? "");
  const relicPurchase = useDraftValue(relicDraftOperation, live.relicPurchaseKey);
  const [pay, setPay] = useState(true);
  const payloadOf = () => {
    const payload: Record<string, unknown> = { instance: item.instance, name: name || null, quantity, quality, equipped, origin, relic, parryWeapon: parry, note: note || null };
    if (relicSplit !== item.relicSplit) payload.relicSplit = relic ? relicSplit : null;
    if ((creature || null) !== item.creature) payload.creature = creature || null;
    if (improvements) payload.improvements = choicePayload(improvements);
    if (powers) payload.legendaryPowers = powers;
    if (fromRelic !== item.materialFromRelic) payload.materialFromRelic = fromRelic;
    if (ammo !== undefined) payload.ammo = ammo;
    if (item.kind === "ammo" && (projectileOptions.length > 0 || item.projectile)) payload.projectile = projectile || null;
    payload.artifact = artifact;
    payload.material = material?.id ?? null;
    payload.materialPrice = material ? materialPrice.trim() || null : null;
    payload.materialParam = materialChoice ? materialParam : null;
    payload.personalizations = choicePayload(personalizations);
    payload.personalizedBySelf = ownWorkPossible && personalizedBySelf;
    payload.blessed = blessed;
    payload.fullSet = fullSet;
    if (item.variantOptions) payload.variants = variants;
    payload.crafter = crafter;
    payload.price = price.trim() || null;
    payload.load = load.trim() || null;
    payload.hardness = hardness.trim() || null;
    if (baseEquipment !== item.baseEquipment) payload.baseEquipment = baseEquipment;
    if (parent !== item.parent) payload.parent = parent;
    if (needsFocus || item.focus) payload.focus = focusSkill && focusName.trim() ? { skill: focusSkill, name: focusName.trim() } : null;
    if (!creation) payload.pay = pay;
    return payload;
  };
  // In career mode the price after the draft shows what an adjustment costs, e.g. at the smith (konzept-gegenstaende E5). Place and worn
  // leave the price as it is, so the preview keeps the line where and as it lies: arriving at an equal line it would join it and lose its
  // price, and worn without the move to the body the engine refuses it.
  const pricePayload = () => {
    const payload: Record<string, unknown> = { ...payloadOf(), pay: false };
    delete payload.parent;
    delete payload.equipped;
    return payload;
  };
  const draftPrice = useDraftValue(creation ? null : { type: "sm.updateItem", payload: pricePayload() }, live.priceKey);
  // Setting up a carrier is an operation of its own; it waits until the draft is saved, so a choice made here, e.g. of Kreatur, is not lost.
  const [savedPayload] = useState(() => JSON.stringify(payloadOf()));
  const dirty = JSON.stringify(payloadOf()) !== savedPayload;
  const savedPrice = value(live.priceKey)?.number ?? null;
  const save = async () => {
    if (busy) return;
    const operation = { type: "sm.updateItem", payload: payloadOf() };
    setBusy(true);
    try {
      // What is booked, and whether the money suffices, shows the preview before anything is booked; without a booking it saves at once.
      // A preview that fails goes to the confirmation as well, which shows why. A new material is paid as well (MSK p. 94).
      const books = !creation && (pay || materialPaid || personalizationAdded) && (await previewOperation(character.id, operation).then((p) => p.bookings.length > 0, () => true));
      if (books) {
        confirm(operation, materialPaid ? "Speichern" : "Anpassen");
        onClose();
      } else if (await apply(operation)) onClose();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet
      title={item.name}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button danger" onClick={async () => (await apply({ type: "sm.removeItem", payload: { instance: item.instance } })) && onClose()}>
            Entfernen
          </button>
          {!creation && live.saleKey && live.quantity > 0 && (
            <button type="button" className="button secondary" onClick={() => setSelling(true)}>
              Verkaufen
            </button>
          )}
          <button type="button" className="button primary" disabled={busy} onClick={() => void save()}>
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorNote error={error} />
        <p className="muted small">
          {kindLabels[item.kind]}
          {item.listPrice != null && <> · Listenpreis {item.listPriceText}</>}
          {item.load != null && <> · Last {number(item.load)}</>}
        </p>
        {live.custom && (
          <div className="inline-form two">
            <p className="muted small">
              Eigenbau mit Werten{live.custom.usedBy > 1 ? `, gilt für ${live.custom.usedBy} Zeilen` : ""}: mit der Spielleitung abstimmen, nach jeder Änderung der Werte erneut.
            </p>
            <button type="button" className="button secondary" onClick={() => setEditingValues(true)}>
              Werte bearbeiten
            </button>
          </div>
        )}
        {editingValues && live.custom && (
          <Sheet title={`Werte: ${live.name}`} onClose={() => setEditingValues(false)} wide>
            <div className="stack small-gap">
              <ErrorNote error={error} />
              <CustomValuesForm
                kind={live.custom.kind}
                initial={live.custom.values}
                submitLabel="Werte speichern"
                busy={busy}
                onSubmit={async (entry) => {
                  if (await apply({ type: "sm.updateCustomEntry", payload: { id: live.custom!.id, entry } })) setEditingValues(false);
                }}
              />
            </div>
          </Sheet>
        )}
        <label className="field">
          <span>Eigener Name</span>
          <input value={name} placeholder={item.name} onChange={(e) => setName(e.target.value)} />
        </label>
        {targets && item.role !== "carrier" && (
          <label className="field">
            <span>Ort</span>
            <select value={parent ?? ""} onChange={(e) => setParent(e.target.value)}>
              {targets.map((t) => (
                <option key={t.id} value={t.id} disabled={!t.allowed && !t.current}>
                  {"\u00a0\u00a0".repeat(t.depth)}
                  {t.label}
                  {t.allowed || t.current ? "" : ` (${t.reason})`}
                </option>
              ))}
            </select>
          </label>
        )}
        {creation && (
          <label className="field">
            <span>Herkunft (bestimmt, aus welchem Budget der Gegenstand bezahlt wird)</span>
            <select value={origin} onChange={(e) => setOrigin(e.target.value as ItemOrigin)}>
              {[...view.startKit.origins, ...(view.startKit.origins.some((o) => o.id === item.origin) ? [] : [{ id: item.origin, label: item.originLabel }])].map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        )}
        {item.kind !== "ammo" && (relics.length > 0 || item.relic) && (
          <div className="stack small-gap">
            <label className="field">
              <span>Relikt</span>
              <select
                value={relic ?? ""}
                onChange={(e) => {
                  const next = e.target.value || null;
                  setRelic(next);
                  // A relic is not bought (GRW p. 79); during creation it leaves the start money unless the origin is set again.
                  if (next && creation && origin === "purchased") setOrigin("received");
                }}
              >
                <option value="">kein Relikt</option>
                {relics.map((r) => (
                  <option key={r.key} value={r.key}>
                    {resourceLabel(view, r.key)} ({display(value(r.value))})
                  </option>
                ))}
                {item.relic && !relics.some((r) => r.key === item.relic) && <option value={item.relic}>{item.relicLabel ?? item.relic} (nicht vorhanden)</option>}
              </select>
            </label>
            {chosenRelic?.relicPoints && (
              <BudgetBar
                label="Reliktpunkte"
                used={relicSpent ?? 0}
                total={budget(chosenRelic.relicPoints.total)}
                usedText={relicDraft ? undefined : <Value valueKey={chosenRelic.relicPoints.spent} />}
                totalText={<Value valueKey={chosenRelic.relicPoints.total} />}
                hint={`Jeder Punkt bezahlt eine Stufe Qualität oder Artefaktqualität (${page(chosenRelic.source)}); was darüber hinausgeht, wird zugekauft.`}
              />
            )}
            {chosenRelic && (
              <div className="stack small-gap" role="group" aria-label="Aufteilung der Reliktpunkte">
                <label className="option">
                  <input
                    type="checkbox"
                    checked={relicSplit !== null}
                    onChange={(e) => setRelicSplit(e.target.checked ? { quality: Math.max(quality, 0), artifactQuality: 0 } : null)}
                  />
                  <span>Aufteilung selbst festlegen</span>
                </label>
                {relicSplit ? (
                  <>
                    <div className="row spread">
                      <span>aus Reliktpunkten: Qualität</span>
                      <Stepper label="Qualität aus Reliktpunkten" value={relicSplit.quality} min={0} onChange={(q) => setRelicSplit({ ...relicSplit, quality: q })} />
                    </div>
                    <div className="row spread">
                      <span>aus Reliktpunkten: Artefaktqualität</span>
                      <Stepper
                        label="Artefaktqualität aus Reliktpunkten"
                        value={relicSplit.artifactQuality}
                        min={0}
                        onChange={(q) => setRelicSplit({ ...relicSplit, artifactQuality: q })}
                      />
                    </div>
                    <span className="muted small">Dieses Relikt bekommt seine Punkte vor den anderen der Instanz; der Rest wird zugekauft.</span>
                  </>
                ) : (
                  <span className="muted small">Ohne eigene Aufteilung gehen die Punkte der Reihe nach auf die Relikte, je Gegenstand erst auf die Qualität.</span>
                )}
              </div>
            )}
            {chosenRelic && (relicDraft ? (relicPurchase ?? 0) > 0 : (value(live.relicPurchaseKey)?.number ?? 0) > 0) && (
              <div className="stack small-gap">
                {relicDraft ? (
                  <p className="small">
                    Zukauf über die Reliktpunkte hinaus: <strong>{money(relicPurchase)}</strong>
                  </p>
                ) : (
                  <ValueRow valueKey={live.relicPurchaseKey} label="Zukauf über die Reliktpunkte hinaus" />
                )}
                <p className="muted small">
                  Bezahlt wird der Unterschied der Aufschläge zur Qualität aus Reliktpunkten (GRW S. 143)
                  {creation ? (origin === "tools" ? ", aus dem Wert des Handwerkszeugs" : origin === "startWeapon" ? ", aus dem Wert der Startwaffe" : ", vom Startvermögen") : ""}. Sprich den
                  Zukauf mit der Spielleitung ab.
                </p>
              </div>
            )}
            {relic && material && (material.quality ?? 0) > 0 && (
              <label className="option">
                <input type="checkbox" checked={fromRelic} disabled={materialPointBlocked} onChange={(e) => setMaterialFromRelic(e.target.checked)} />
                <span>
                  Materialeigenschaft von {material.name} mit einem Reliktpunkt bezahlen
                  <span className="muted small">
                    {" "}
                    {materialPointBlocked
                      ? "Nicht möglich: Der Punkt ginge über den Höchstwert der Ressource Relikt; der Aufpreis wird in Lunaren bezahlt."
                      : `Statt des Aufpreises in Lunaren; die Reliktstufe ohne diesen Punkt muss mindestens ${material.quality} betragen (Mondstahlklingen S. 100).`}
                  </span>
                </span>
              </label>
            )}
          </div>
        )}
        {item.kind !== "ammo" && item.kind !== "custom" && ((relic && view.legendaryPowers.length > 0) || currentPowers.length > 0) && (
          <RelicPowers item={live} kinds={improvementKinds[item.kind]} powers={currentPowers} onChange={setPowers} changed={powers !== null} total={powerCost} isRelic={relic !== null} />
        )}
        {item.kind !== "ammo" && (
          <CreatureField
            item={live}
            value={creature}
            onChange={(next) => {
              setCreature(next);
              // An animal from a resource is not bought (GRW p. 78); during creation it leaves the start money unless the origin is set again.
              if (next && creation && origin === "purchased") setOrigin("received");
            }}
          />
        )}
        {live.canCarry && (
          <div className="inline-form two">
            <p className="muted small">
              {dirty
                ? "Erst speichern; danach lässt es sich als Lasttier oder Fahrzeug einrichten."
                : "Ein eigenes Tier oder Fahrzeug kann Last tragen; es ist dann ein eigener Ort, und was darauf liegt, zählt nicht zur Traglast des Abenteurers."}
            </p>
            <button
              type="button"
              className="button secondary"
              disabled={busy || dirty}
              onClick={async () => (await choose({ type: "sm.setCarrier", payload: { instance: item.instance, carrier: true } }, "Als Lasttier eingerichtet")) && onClose()}
            >
              Als Lasttier oder Fahrzeug einrichten
            </button>
          </div>
        )}
        <div className="row spread">
          <span>{item.kind === "ammo" ? "Übrig" : "Anzahl"}</span>
          <Stepper label="Anzahl" value={quantity} min={item.kind === "ammo" ? 0 : 1} onChange={setQuantity} />
        </div>
        {item.kind === "ammo" && <p className="muted small">{usedBy.length > 0 ? `Zugeordnet: ${usedBy.map((w) => w.customName ?? w.name).join(", ")}` : "Noch keiner Waffe zugeordnet."}</p>}
        {item.kind === "ammo" && (projectileOptions.length > 0 || item.projectile) && (
          <ProjectileField projectiles={projectileOptions} value={projectile} onChange={setProjectile} />
        )}
        {item.kind === "ammo" && live.availabilityKey && <ValueRow valueKey={live.availabilityKey} label="Verfügbarkeit" />}
        {item.kind === "ammo" && live.craftKeys?.schwierigkeit && (
          <ValueRow valueKey={live.craftKeys.schwierigkeit} label="Schwierigkeit der Herstellung" />
        )}
        {item.kind === "ammo" && live.craftKeys?.fortschritt && (
          <ValueRow valueKey={live.craftKeys.fortschritt} label="Fortschrittspunkte" />
        )}
        {item.usesAmmo && (
          <div className="stack small-gap">
            <label className="field">
              <span>Munition</span>
              <select value={(ammo === undefined ? live.ammo : ammo) ?? ""} onChange={(e) => setAmmo(e.target.value || null)}>
                <option value="">keine</option>
                {stocks.map((s) => (
                  <option key={s.instance} value={s.instance}>
                    {s.customName ?? s.name} ({s.quantity})
                  </option>
                ))}
              </select>
            </label>
            <div className="actions">
              <button type="button" className="button secondary" onClick={() => setAddingAmmo(true)}>
                + Munition anlegen
              </button>
            </div>
          </div>
        )}
        {item.kind !== "custom" && item.kind !== "ammo" && (
          <section className="editor-block stack small-gap">
            <div className="row spread">
              <span>
                <strong>Qualität</strong>{" "}
                <span className="muted small">{relic ? `bezahlt mit Punkten von ${resourceLabel(view, relic)}` : "Preisaufschlag nach GRW S. 181"}</span>
              </span>
              {/* Negative quality has no lower bound (GRW p. 142; SM-E-088). */}
              <Stepper label="Qualität" value={quality} onChange={setQuality} />
            </div>
            {live.availabilityKey && <ValueRow valueKey={live.availabilityKey} label="Verfügbarkeit mit Qualität" />}
            {withImprovements && (
              <>
                <BudgetBar
                  label={reversed ? "Umgekehrte Verbesserungen" : "Verbesserungen"}
                  used={qualitySpent ?? 0}
                  usedText={qualityDraft ? undefined : <Value valueKey={live.qualityKey} />}
                  total={qualityBudget ?? 0}
                  totalText={qualityDraft || qualityBudget === null ? undefined : <Value valueKey={live.qualityBudgetKey} />}
                  hint={
                    reversed
                      ? `Negative Qualität kehrt die gewählten Verbesserungen um, zu denselben Kosten (GRW S. 142); der Preis sinkt um ${view.startKit.negativeQualityPercent ?? "?"} % je Stufe (S. 181).`
                      : "Jede Qualitätsstufe wird auf Verbesserungen verteilt (GRW S. 142)."
                  }
                />
                <List aria-label="Verbesserungen">
                  {currentImprovements.length === 0 && <li className="list-empty">Noch keine Verbesserung gewählt.</li>}
                  {currentImprovements.map((imp, index) => {
                    const choice = improvementChoices.get(imp.id);
                    const update = (change: Partial<ItemChoiceRow>) => setImprovements(currentImprovements.map((r, i) => (i === index ? { ...r, ...change } : r)));
                    return (
                      <ListRow
                        key={`${imp.id}-${index}`}
                        title={imp.name}
                        value={`${imp.qualityCost ?? 1} Q`}
                        actions={
                          <button type="button" className="link danger" onClick={() => setImprovements(currentImprovements.filter((_, i) => i !== index))}>
                            Entfernen
                          </button>
                        }
                        below={
                          choice?.parameter || choice?.detail ? (
                            <div className="stack small-gap">
                              {choice.parameter && <ParamField param={choice.parameter} value={imp.param} label={`${imp.name} ${index + 1}`} onChange={(param) => update({ param })} />}
                              {choice.detail && <ParamField param={choice.detail} value={imp.detail} label={`${imp.name} ${index + 1} ${choice.detail.label}`} onChange={(detail) => update({ detail })} />}
                            </div>
                          ) : undefined
                        }
                      />
                    );
                  })}
                </List>
                {item.grantedImprovements.length > 0 && <p className="muted small">Durch das Material ohne Qualität: {item.grantedImprovements.join(", ")} (Mondstahlklingen S. 94).</p>}
                <div className="actions">
                  <button type="button" className="button secondary" onClick={() => setPicking(true)}>
                    + Verbesserung wählen
                  </button>
                </div>
                {needsFocus && (
                  <div className="stack small-gap" role="group" aria-label="Schwerpunkt des Fertigkeitsbonus">
                    <div className="inline-form two">
                      <label className="field">
                        <span>Fertigkeit</span>
                        <select value={focusSkill} onChange={(e) => setFocusSkill(e.target.value)}>
                          <option value="">nicht festgelegt</option>
                          {view.skills
                            .filter((s) => s.category !== "kampf")
                            .map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                        </select>
                      </label>
                      <label className="field">
                        <span>Schwerpunkt</span>
                        <input value={focusName} placeholder="z. B. Klettern" onChange={(e) => setFocusName(e.target.value)} />
                      </label>
                    </div>
                    <span className="muted small">
                      Der Bonus gilt nur für diesen Schwerpunkt und nur in passenden Lagen; die Spielleitung legt ihn nach dem Einsatzgebiet fest (GRW S. 142).
                    </span>
                  </div>
                )}
              </>
            )}
            {!withImprovements && (
              <p className="muted small">Mit Qualität 1 oder mehr lassen sich Verbesserungen wählen, etwa „Fertigkeitsbonus +1“ für eine Waffe; negative Qualität kehrt sie um.</p>
            )}
            {((relic && view.blessing) || item.blessed) && (
              <label className="option">
                <input type="checkbox" checked={blessed} onChange={(e) => setBlessed(e.target.checked)} />
                <span>
                  {view.blessing
                    ? `${view.blessing.name}: ${view.blessing.itemQualityBonus} ${view.blessing.itemQualityBonus === 1 ? "Qualitätsstufe" : "Qualitätsstufen"} mehr für Verbesserungen, ohne Preis und Reliktpunkte (${page(view.blessing.source)})`
                    : "Gesegnet: Die Regeldaten nennen keinen eindeutigen Zauber, die Segnung zählt nichts."}
                </span>
              </label>
            )}
          </section>
        )}
        {item.kind !== "custom" && item.kind !== "ammo" && (
          <section className="editor-block stack small-gap" aria-label="Material und Personalisierung">
            <div className="row spread">
              <span>
                <strong>Material</strong> <span className="muted small">{material?.name ?? "nicht angegeben"}</span>
              </span>
              <span className="row">
                <button type="button" className="link" onClick={() => setPickingMaterial(true)}>
                  {material ? "Ändern" : "Wählen"}
                </button>
                {material && (
                  <button type="button" className="link danger" onClick={() => setMaterial(null)}>
                    Entfernen
                  </button>
                )}
              </span>
            </div>
            {material?.summary && <p className="muted small">{material.summary}</p>}
            {material && (material.quality ?? 0) > quality && (
              <p className="muted small">
                {relic
                  ? `Qualität mindestens ${material.quality} durch ${material.name} (Mondstahlklingen S. 94); bei einem Relikt gibt das weder Plätze für Verbesserungen noch einen Zukauf (S. 100).`
                  : `Qualität mindestens ${material.quality} durch ${material.name} (Mondstahlklingen S. 94); Preis und Reliktpunkte rechnen damit.`}
              </p>
            )}
            {material && (
              <label className="field">
                <span>Aufpreis Material je Stück</span>
                <input
                  value={materialPrice}
                  placeholder={materialSuggestion == null ? "z. B. 15 L" : `Vorschlag ${money(materialSuggestion)}`}
                  onChange={(e) => setMaterialPrice(e.target.value)}
                />
                <span className="muted small">
                  {creation
                    ? "Der Vorschlag kommt aus dem Aufpreis je Punkt Last; ein eigener Betrag, etwa nach Rabatt oder Aufschlag, ersetzt ihn im Preis."
                    : "Beim Speichern wird der Betrag nach einer Vorschau als Geld gebucht, beim Wechsel des Materials ganz, sonst der Unterschied; 0 bucht nichts."}
                </span>
              </label>
            )}
            {material && materialChoice && <ParamField param={materialChoice} value={materialParam} label={material.name} onChange={setMaterialParam} />}
            {item.variantOptions && (
              <div className="stack small-gap" role="group" aria-label="Ausführung">
                <strong>Ausführung</strong>
                {item.variantOptions.map((v) => (
                  <label key={v.id} className="option">
                    <input
                      type="checkbox"
                      checked={variants.includes(v.id)}
                      onChange={(e) => setVariants(e.target.checked ? [...variants, v.id] : variants.filter((x) => x !== v.id))}
                    />
                    <span>
                      {v.name} (+{v.price}
                      {v.availability ? `, ${v.availability}` : ""}
                      {v.granted.length > 0 ? `, mit ${v.granted.join(", ")}` : ""})
                    </span>
                  </label>
                ))}
              </div>
            )}
            {(kinds.includes("kleidung") || item.kind === "armor" || item.fullSet) && (
              <label className="option">
                <input type="checkbox" checked={fullSet} onChange={(e) => setFullSet(e.target.checked)} />
                <span>voller Satz: manche Materialien wirken nur so (Mondstahlklingen S. 95)</span>
              </label>
            )}
            {(offeredPersonalizations.length > 0 || personalizations.length > 0) && (
              <div className="stack small-gap" role="group" aria-label="Personalisierung">
                <strong>Personalisierung</strong>
                {!view.personalizationAllowed && <p className="muted small">Nach den Regeloptionen dieses Charakters wirkt die Personalisierung nicht (Mondstahlklingen S. 99).</p>}
                <List aria-label="Gewählte Personalisierungen">
                  {personalizations.length === 0 && <li className="list-empty">Nicht personalisiert.</li>}
                  {personalizations.map((row, index) => {
                    const def = personalizationDefs.get(row.id);
                    const update = (change: Partial<ItemChoiceRow>) => setPersonalizations(personalizations.map((r, i) => (i === index ? { ...r, ...change } : r)));
                    return (
                      <ListRow
                        key={`${row.id}-${index}`}
                        title={def?.name ?? row.name}
                        actions={
                          <button type="button" className="link danger" onClick={() => setPersonalizations(personalizations.filter((_, i) => i !== index))}>
                            Entfernen
                          </button>
                        }
                        below={
                          <div className="stack small-gap">
                            {def?.parameter && <ParamField param={def.parameter} value={row.param} label={`${def.name} ${index + 1}`} onChange={(param) => update({ param })} />}
                            {def?.detail && <ParamField param={def.detail} value={row.detail} label={`${def.name} ${index + 1} ${def.detail.label}`} onChange={(detail) => update({ detail })} />}
                            {def?.summary && <span className="muted small">{def.summary} ({page(def.source)})</span>}
                          </div>
                        }
                      />
                    );
                  })}
                </List>
                {offeredPersonalizations.length > 0 && (
                  <label className="field">
                    <span>Personalisierung hinzufügen</span>
                    <select
                      value=""
                      onChange={(e) => {
                        const def = personalizationDefs.get(e.target.value);
                        if (def) setPersonalizations([...personalizations, newRow(def.id, def.name)]);
                      }}
                    >
                      <option value="">wählen …</option>
                      {offeredPersonalizations.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {ownWorkPossible && (
                  <label className="option">
                    <input type="checkbox" checked={personalizedBySelf} onChange={(e) => setPersonalizedBySelf(e.target.checked)} />
                    <span>selbst personalisiert: nur das Material wird bezahlt (Mondstahlklingen S. 99)</span>
                  </label>
                )}
              </div>
            )}
          </section>
        )}
        {item.kind !== "custom" && item.kind !== "ammo" && <CrafterFields item={live} crafter={crafter} onChange={setCrafter} />}
        {item.wearable && (
          <label className="option">
            <input type="checkbox" checked={equipped} onChange={(e) => setEquipped(e.target.checked)} />
            <span>angelegt</span>
          </label>
        )}
        {view.carrying && (
          <label className="option">
            <input type="checkbox" checked={baseEquipment} onChange={(e) => setBaseEquipment(e.target.checked)} />
            <span>zählt zur Grundausstattung und nicht zur Traglast ({page(view.carrying.source)}); das legt die Spielleitung fest</span>
          </label>
        )}
        {item.kind === "weapon" && (
          <label className="option">
            <input type="checkbox" checked={parry} onChange={(e) => setParry(e.target.checked)} />
            <span>als Parierwaffe in der Nebenhand (Behinderung nach Merkmal, GRW S. 186)</span>
          </label>
        )}
        {item.kind !== "ammo" && (
          <label className="option">
            <input type="checkbox" checked={artifact !== null} onChange={(e) => setArtifact(e.target.checked ? { quality: 1, properties: [] } : null)} />
            <span>Artefakt (GRW S. 254)</span>
          </label>
        )}
        {artifact && <ArtifactFields value={artifact} onChange={setArtifact} known={item.artifactProperties} />}
        <div className="inline-form two">
          <label className="field">
            <span>Preis je Stück</span>
            <input value={price} placeholder={item.listPrice == null ? "z. B. 3 L 50 T" : `Listenpreis ${item.listPriceText}`} onChange={(e) => setPrice(e.target.value)} />
          </label>
          <label className="field">
            <span>Last</span>
            <input
              inputMode="decimal"
              value={load}
              placeholder={item.kind !== "custom" && item.kind !== "ammo" && item.load != null && item.customLoad == null ? `aus der Liste: ${number(item.load)}` : "z. B. 1 oder 0,5"}
              onChange={(e) => setLoad(e.target.value)}
            />
          </label>
        </div>
        {item.kind !== "ammo" && (
          <div className="stack small-gap">
            <label className="field">
              <span>Härte</span>
              <input
                inputMode="numeric"
                value={hardness}
                placeholder={item.listHardness != null ? `aus der Liste: ${item.listHardness}` : "z. B. 3"}
                onChange={(e) => setHardness(e.target.value)}
              />
            </label>
            {live.hardnessKey && (
              <div className="inline-form two">
                <ValueRow valueKey={live.hardnessKey} label="Härte" />
                {live.durabilityKey && <ValueRow valueKey={live.durabilityKey} label="Haltbarkeit" />}
              </div>
            )}
            <span className="muted small">
              {item.listHardness != null && view.startKit.hardnessVariation != null
                ? `Die Härte schwankt je nach Verarbeitung um bis zu ${view.startKit.hardnessVariation} (${page(view.startKit.durabilitySource)}); eine größere Abweichung von der Liste ist eine Warnung.`
                : item.listHardness == null && item.kind !== "custom"
                  ? `Die Liste nennt keine feste Härte; sie lässt sich hier je Exemplar setzen. Haltbarkeit ist Last plus Härte (${page(view.startKit.durabilitySource)}).`
                  : `Haltbarkeit ist Last plus Härte (${page(view.startKit.durabilitySource)}).`}
            </span>
          </div>
        )}
        <label className="field">
          <span>Notiz</span>
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        {!creation && (
          <section className="editor-block stack small-gap" aria-label="Anpassen">
            {draftPrice !== savedPrice && (
              <p className="small">
                Preis je Stück: {money(savedPrice)} → <strong>{money(draftPrice)}</strong>
              </p>
            )}
            <label className="option">
              <input type="checkbox" checked={pay} onChange={(e) => setPay(e.target.checked)} />
              <span>Bezahlen</span>
            </label>
            <span className="muted small">
              Wird der Gegenstand teurer, etwa beim Schmied, bucht das Speichern den Unterschied vom Geld, nach einer Vorschau. Qualität aus Reliktpunkten kostet nichts; ein billigerer
              Gegenstand erstattet nichts.
            </span>
          </section>
        )}
      </div>
      {selling && (
        <SellSheet
          item={live}
          onClose={(sold) => {
            setSelling(false);
            if (sold) onClose();
          }}
        />
      )}
      {addingAmmo && <AddItemSheet title="Munition anlegen" kinds={["ammo"]} forWeapon={item} onClose={() => setAddingAmmo(false)} />}
      {pickingMaterial && (
        <CatalogPicker
          type="materials"
          title="Material"
          unavailable={(m) => (fits((m.details as { appliesTo?: string[] } | undefined)?.appliesTo ?? []) ? null : `nicht für ${kindLabels[item.kind]}`)}
          onClose={() => setPickingMaterial(false)}
          onPick={(m) => {
            setPickingMaterial(false);
            const details = m.details as { quality?: number; summary?: string } | undefined;
            setMaterial({ id: m.id, name: m.label.de, quality: details?.quality ?? null, summary: details?.summary ?? null });
            setMaterialPrice("");
          }}
        />
      )}
      {picking && (
        <CatalogPicker
          type="improvements"
          title="Verbesserung"
          hint="Der Wert rechts sind die Qualitätsstufen, die eine Verbesserung kostet (GRW S. 142)."
          unavailable={(i) => {
            const applies = ((i.details as { appliesTo?: string[] } | undefined)?.appliesTo ?? []);
            return applies.length > 0 && !applies.some((a) => kinds.includes(a)) ? `nicht für ${kindLabels[item.kind]}` : null;
          }}
          onClose={() => setPicking(false)}
          onPick={(i) => {
            setPicking(false);
            setImprovements([...currentImprovements, newRow(i.id, i.label.de, (i.details as { qualityCost?: number } | undefined)?.qualityCost)]);
          }}
        />
      )}
    </Sheet>
  );
}

/**
 * Sells all or some pieces of a line in career mode. Proposed is the share of the reference price the rules give for used goods
 * (GRW p. 110); a change in percent or an amount replaces it like when buying. The preview shows the booking before anything is booked.
 */
function SellSheet({ item, onClose }: { item: InventoryView; onClose: (sold: boolean) => void }) {
  const { view, value, display, money, confirm } = useSm();
  const [quantity, setQuantity] = useState(item.quantity);
  const [adjust, setAdjust] = useState("");
  const [received, setReceived] = useState("");
  const [trade, setTrade] = useState<TradeState>(emptyTrade);
  const perPiece = item.saleKey ? (value(item.saleKey)?.number ?? null) : null;
  const relic = item.relic ? view.resources.find((r) => r.key === item.relic) : undefined;
  const relicValue = relic ? (value(relic.value)?.number ?? 0) : 0;
  const [lowerRelic, setLowerRelic] = useState(relicValue > 0);
  const [relicPoints, setRelicPoints] = useState(Math.min(relicValue, Math.max(1, item.relicPointsKey ? (value(item.relicPointsKey)?.number ?? 0) : 0)));
  const name = item.customName ?? item.name;
  const sell = () => {
    confirm(
      {
        type: "sm.sellItem",
        payload: {
          instance: item.instance,
          quantity,
          adjust: adjust.trim() || null,
          received: received.trim() || null,
          lowerRelic: relic && lowerRelic ? relicPoints : null,
          trade: tradePayload(trade, view.trading !== null),
        },
      },
      "Verkaufen",
    );
    onClose(true);
  };
  return (
    <Sheet
      title={`${name} verkaufen`}
      onClose={() => onClose(false)}
      footer={
        <>
          <button type="button" className="button secondary" onClick={() => onClose(false)}>
            Abbrechen
          </button>
          <button type="button" className="button primary" onClick={sell}>
            Verkaufen
          </button>
        </>
      }
    >
      <div className="stack small-gap">
        {item.quantity > 1 && (
          <div className="row spread">
            <span>Anzahl</span>
            <Stepper label="Anzahl" value={quantity} min={1} max={item.quantity} onChange={setQuantity} />
          </div>
        )}
        <p className="small">
          Referenzpreis <Value valueKey={item.referenceKey} />
          {item.quantity > 1 && " je Stück"}
        </p>
        <TradeFields buying={false} value={trade} onChange={setTrade} />
        <div className="inline-form two">
          <label className="field">
            <span>Preisänderung in %</span>
            <input inputMode="decimal" value={adjust} placeholder="z. B. +10" onChange={(e) => setAdjust(e.target.value)} />
          </label>
          <label className="field">
            <span>Erlös</span>
            <input value={received} placeholder={perPiece == null ? "Betrag" : money(perPiece * quantity)} onChange={(e) => setReceived(e.target.value)} />
          </label>
        </div>
        <span className="muted small">
          Vorgeschlagen ist {view.startKit.saleShare ?? "ein Teil"} des Referenzpreises mit Qualität und Artefaktqualität, die Verhandlungsbasis für Gebrauchtware ({page(view.startKit.saleSource)}),
          geändert um die Prozentangabe; ein Betrag unter „Erlös“ ersetzt beides.
        </span>
        {relic && (
          <div className="stack small-gap">
            <p className="notice warning-box small" role="note">
              {name} ist ein Relikt: Mit dem Verkauf entfallen die Punkte der Ressource {resourceLabel(view, relic.key)} ({display(value(relic.value))}), die es verkörpert ({page(relic.source)}).
            </p>
            {relicValue > 0 && (
              <div className="row spread">
                <label className="option">
                  <input type="checkbox" checked={lowerRelic} onChange={(e) => setLowerRelic(e.target.checked)} />
                  <span>{resourceLabel(view, relic.key)} senken um</span>
                </label>
                <Stepper label="Punkte" value={relicPoints} min={1} max={relicValue} onChange={setRelicPoints} />
              </div>
            )}
          </div>
        )}
      </div>
    </Sheet>
  );
}

/** Clears a rejected input when the sheet closes, so the message does not stay on the page behind it. */
export function useClearErrorOnClose(clearError: () => void) {
  const clear = useRef(clearError);
  clear.current = clearError;
  useEffect(() => () => clear.current(), []);
}

/** What the table rolled when trading; the engine computes the price from it (MSK p. 104–105; GRW p. 111). */
interface TradeState {
  market: string;
  finding: string;
  outcome: string;
  levels: number;
  quick: string;
  /** By the rules: the trade is on the black market, where a mastery for it acts (GRW p. 132; SM-RA-060). */
  blackMarket: boolean;
  /** With the optional rule: a purchase in the month after a fumbled search for a merchant (MSK p. 104). */
  afterFumble: boolean;
}

const emptyTrade: TradeState = { market: "", finding: "ohne-probe", outcome: "", levels: 0, quick: "", blackMarket: false, afterFumble: false };

const findings = [
  { value: "ohne-probe", label: "ohne Probe (am Ort verfügbar)" },
  { value: "knapp-misslungen", label: "knapp misslungen" },
  { value: "gelungen", label: "gelungen" },
  { value: "herausragend", label: "herausragend" },
  { value: "misslungen", label: "misslungen (kein Händler)" },
  { value: "verheerend", label: "verheerend (kein Händler)" },
];

const outcomes = [
  { value: "", label: "nicht gefeilscht" },
  { value: "herausragend", label: "herausragend" },
  { value: "gelungen", label: "gelungen" },
  { value: "knapp-misslungen", label: "knapp misslungen" },
  { value: "misslungen", label: "misslungen" },
  { value: "verheerend", label: "verheerend" },
];

/** The trade the operation takes, or null when nothing was entered. */
function tradePayload(state: TradeState, market: boolean): Record<string, unknown> | null {
  if (state.quick) return { quick: state.quick };
  const haggle = state.outcome ? { outcome: state.outcome, successLevels: state.levels } : null;
  if (market) return { market: state.market || null, finding: state.finding, ...(haggle ? { haggle } : {}), ...(state.afterFumble ? { afterFumble: true } : {}) };
  return haggle ? { haggle, ...(state.blackMarket ? { blackMarket: true } : {}) } : null;
}

/** Market, search for a merchant and haggling as the table rolled them; with the optional rule also the quick sale. */
function TradeFields(props: { buying: boolean; value: TradeState; onChange: (state: TradeState) => void }) {
  const { view } = useSm();
  const trading = view.trading;
  if (!trading && !view.haggling) return null;
  const set = (change: Partial<TradeState>) => props.onChange({ ...props.value, ...change });
  const quick = !props.buying && trading && trading.quickSale.length > 0;
  return (
    <div className="stack small-gap" role="group" aria-label="Handel">
      {quick && (
        <label className="field">
          <span>Schnelle Methode</span>
          <select value={props.value.quick} onChange={(e) => set({ quick: e.target.value })}>
            <option value="">nein</option>
            {trading.quickSale.map((q) => (
              <option key={q.id} value={q.id}>
                {q.name} ({q.percent} %)
              </option>
            ))}
          </select>
        </label>
      )}
      {!props.value.quick && trading && (
        <div className="inline-form two">
          <label className="field">
            <span>Markt</span>
            <select value={props.value.market || trading.markets[0]?.id} onChange={(e) => set({ market: e.target.value })}>
              {trading.markets.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Händlersuche</span>
            <select value={props.value.finding} onChange={(e) => set({ finding: e.target.value })}>
              {findings.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      {!props.value.quick && (
        <div className="inline-form two">
          <label className="field">
            <span>Feilschen</span>
            <select value={props.value.outcome} onChange={(e) => set({ outcome: e.target.value })}>
              {outcomes.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {props.value.outcome && (
            <div className="field">
              <span>Erfolgsgrade</span>
              <Stepper label="Erfolgsgrade" value={props.value.levels} min={0} onChange={(levels) => set({ levels })} />
            </div>
          )}
        </div>
      )}
      {!trading && view.blackMarketMastery && props.value.outcome && (
        <label className="option">
          <input type="checkbox" checked={props.value.blackMarket} onChange={(e) => set({ blackMarket: e.target.checked })} />
          <span>auf dem Schwarzmarkt (mit {view.blackMarketMastery})</span>
        </label>
      )}
      {props.buying && trading?.fumbleSurcharge && !props.value.quick && (
        <label className="option">
          <input type="checkbox" checked={props.value.afterFumble} onChange={(e) => set({ afterFumble: e.target.checked })} />
          <span>
            {trading.fumbleSurcharge.months === 1 ? "im Monat" : `in den ${trading.fumbleSurcharge.months} Monaten`} nach einer verheerenden Händlersuche: Listenpreis +
            {trading.fumbleSurcharge.percent} %
          </span>
        </label>
      )}
      <span className="muted small">
        {trading
          ? `Den Anteil am Listenpreis rechnet die App nach Markt, Händlersuche und Feilschen (${page(trading.source)}); die Vorschau zeigt ihn.`
          : `Feilschen ändert den Preis nach ${page(view.haggling)}; die Vorschau zeigt den Betrag.`}
      </span>
    </div>
  );
}

/** A choice within a kit with the names of its options, e.g. Stundenglas or Sanduhr (MSK p. 80). */
interface KitChoiceView {
  id: string;
  label: string;
  options: string[];
}

/** The kind of special projectile of a stock (optional rule, MSK p. 35–37); the price follows from the preview, not from here. */
function ProjectileField(props: { projectiles: ProjectileView[]; value: string; onChange: (id: string) => void }) {
  const chosen = props.projectiles.find((p) => p.id === props.value);
  return (
    <label className="field">
      <span>Projektilart</span>
      <select value={props.value} onChange={(e) => props.onChange(e.target.value)}>
        <option value="">gewöhnlich</option>
        {props.projectiles
          .filter((p) => !p.standard)
          .map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.price ? ` (${p.price})` : ""}
            </option>
          ))}
      </select>
      {chosen?.summary && (
        <span className="muted small">
          {chosen.summary} <span className="source">{page(chosen.source)}</span>
        </span>
      )}
      {chosen && (chosen.availability || chosen.crafting) && (
        <span className="muted small">
          {[chosen.availability && `Verfügbarkeit ${chosen.availability}`, chosen.crafting && `hergestellt mit ${chosen.crafting}`].filter(Boolean).join(" · ")}
        </span>
      )}
    </label>
  );
}

/** Gathers shot projectiles after a fight: misses that hit nothing hard, hits on common targets and the die (MSK p. 36). */
function RecoverSheet(props: { stock: { instance: string; name: string }; die: number; onClose: () => void }) {
  const { confirm } = useSm();
  const [misses, setMisses] = useState(0);
  const [hits, setHits] = useState(0);
  const [roll, setRoll] = useState(1);
  return (
    <Sheet title={`${props.stock.name} aufsammeln`} onClose={props.onClose}>
      <div className="stack small-gap">
        <p className="muted small">Fehlschüsse, die nichts Hartes getroffen haben, bleiben ganz; von den Treffern auf gewöhnliche Ziele bleibt ein Teil nach dem Wurf (Mondstahlklingen S. 36).</p>
        <div className="row spread">
          <span>Fehlschüsse</span>
          <Stepper label="Fehlschüsse" value={misses} min={0} onChange={setMisses} />
        </div>
        <div className="row spread">
          <span>Treffer</span>
          <Stepper label="Treffer" value={hits} min={0} onChange={setHits} />
        </div>
        <div className="row spread">
          <span>Wurf (W{props.die})</span>
          <Stepper label="Wurf" value={roll} min={1} max={props.die} onChange={setRoll} />
        </div>
        <div className="actions">
          <button
            type="button"
            className="button primary"
            onClick={() => {
              void confirm({ type: "sm.recoverAmmo", payload: { instance: props.stock.instance, misses, hits, roll } }, "Aufsammeln");
              props.onClose();
            }}
          >
            Aufsammeln
          </button>
        </div>
      </div>
    </Sheet>
  );
}

/** What is left of the ammunition a weapon uses, with one tap per shot and per picked-up piece. */
export function AmmoCounter({ weapon }: { weapon: WeaponView }) {
  const { apply, view } = useSm();
  const [busy, setBusy] = useState(false);
  const [recovering, setRecovering] = useState(false);
  if (!weapon.usesAmmo) return null;
  if (!weapon.ammo) return <span className="muted small">Keine Munition zugeordnet</span>;
  const stock = weapon.ammo;
  const use = async (change: number) => {
    if (busy) return;
    setBusy(true);
    try {
      await apply({ type: "sm.useAmmo", payload: { instance: stock.instance, change } });
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="ammo-counter small">
      <span>
        {stock.name}: <strong data-ammo={stock.instance}>{stock.quantity}</strong>
      </span>
      <button type="button" className="button secondary small" disabled={busy || stock.quantity === 0} aria-label={`${stock.name}: einen verschießen`} onClick={() => void use(-1)}>
        −1
      </button>
      <button type="button" className="button secondary small" disabled={busy} aria-label={`${stock.name}: einen dazu`} onClick={() => void use(1)}>
        +1
      </button>
      {view.recoveryAllowed && view.recoveryDie && (
        <button type="button" className="button secondary small" onClick={() => setRecovering(true)}>
          Aufsammeln
        </button>
      )}
      {recovering && view.recoveryDie && <RecoverSheet stock={stock} die={view.recoveryDie} onClose={() => setRecovering(false)} />}
    </span>
  );
}
